import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bookmark, Clock, ChevronRight, Loader2, ArrowLeft, ExternalLink, Star } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../utils';

const API_URL = getApiUrl();

export default function StylePicks() {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openArticle, setOpenArticle] = useState(null);
  const [promoters, setPromoters] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPicks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/style-picks`);
      setPicks(res.data.picks || []);
    } catch {
      setPicks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  // Fetch promoters for the Featured Brands section
  useEffect(() => {
    axios.get(`${API_URL}/promoters`)
      .then(res => setPromoters(res.data.promoters || []))
      .catch(() => setPromoters([]));
  }, []);

  const getCurrentWeek = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const weekNum = Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
    return `Week ${weekNum}, ${now.getFullYear()}`;
  };

  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getReadTime = (text) => {
    const words = (text || '').split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  const getMainImage = (pick) => {
    if (pick.images && pick.images.length > 0) return pick.images[0];
    if (pick.image_url) return pick.image_url;
    return null;
  };

  const getAllImages = (pick) => {
    if (pick.images && pick.images.length > 0) return pick.images;
    if (pick.image_url) return [pick.image_url];
    return [];
  };

  // ── Full Article View ──
  if (openArticle) {
    const articleImages = getAllImages(openArticle);
    return (
      <div className="min-h-screen bg-white pt-2">
        {/* Article header bar */}
        <div className="border-b border-stone-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => setOpenArticle(null)}
              className="flex items-center gap-2 text-stone-500 hover:text-stone-800 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Style Picks
            </button>
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          {/* Category + Date */}
          <div className="flex items-center gap-3 mb-6">
            {openArticle.category && (
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-terracotta-600">
                {openArticle.category}
              </span>
            )}
            <span className="w-1 h-1 rounded-full bg-stone-300" />
            <span className="text-xs text-stone-400 uppercase tracking-wide">
              {formatDate(openArticle.published_at)}
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 leading-[1.1] mb-6">
            {openArticle.title}
          </h1>

          {/* Byline */}
          <div className="flex items-center gap-4 pb-8 mb-8 border-b border-stone-200">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-terracotta-400 to-amber-400 flex items-center justify-center text-white font-bold text-sm">
              fD
            </div>
            <div>
              <p className="text-sm font-semibold text-stone-800">fashionDisc Editorial</p>
              <div className="flex items-center gap-2 text-xs text-stone-400">
                <Clock className="w-3 h-3" />
                <span>{getReadTime(openArticle.description)} min read</span>
                {openArticle.week_label && (
                  <>
                    <span>·</span>
                    <span>{openArticle.week_label}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Hero image */}
          {articleImages.length > 0 && (
            <div className="mb-10 -mx-4 sm:mx-0">
              <img
                src={articleImages[0]}
                alt={openArticle.title}
                className="w-full rounded-none sm:rounded-2xl object-cover max-h-[600px]"
              />
            </div>
          )}

          {/* Body */}
          {openArticle.description && (
            <div className="prose prose-stone prose-lg max-w-none mb-10">
              {openArticle.description.split('\n').map((para, i) => (
                para.trim() && <p key={i} className="text-stone-700 leading-relaxed text-lg font-serif">{para}</p>
              ))}
            </div>
          )}

          {/* Additional images as gallery */}
          {articleImages.length > 1 && (
            <div className="mb-10">
              <div className={`grid gap-4 ${articleImages.length === 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3'}`}>
                {articleImages.slice(1).map((img, i) => (
                  <div key={i} className="aspect-[3/4] rounded-2xl overflow-hidden bg-stone-100">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {openArticle.tags?.length > 0 && (
            <div className="border-t border-stone-200 pt-6 mt-10">
              <div className="flex flex-wrap gap-2">
                {openArticle.tags.map(t => (
                  <span key={t} className="px-3 py-1.5 bg-stone-100 text-stone-600 rounded-full text-sm font-medium">
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    );
  }

  // ── Main Feed ──
  return (
    <div className="min-h-screen bg-white">
      {/* ── Masthead ── */}
      <div className="border-b border-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-4xl sm:text-5xl font-black text-stone-900 tracking-tight">
                Style Picks
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-stone-400 uppercase tracking-[0.2em] font-medium">
                  {formatDate(new Date())}
                </span>
                <span className="w-1 h-1 rounded-full bg-stone-300" />
                <span className="text-xs text-stone-400 uppercase tracking-[0.2em] font-medium">
                  Weekly Edition
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Thin red accent line */}
      <div className="h-0.5 bg-gradient-to-r from-terracotta-500 via-terracotta-400 to-amber-400" />

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      ) : picks.length === 0 ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-32 text-center">
          <Bookmark className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-stone-800 mb-2">No Articles Yet</h2>
          <p className="text-stone-400 text-sm">This week's style picks are coming soon. Check back shortly.</p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

          {/* ── All Articles — uniform image-left, text-right ── */}
          <div className="space-y-0 divide-y divide-stone-200">
            {picks.map((pick, i) => (
              <motion.div
                key={pick.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 + i * 0.06 }}
                onClick={() => setOpenArticle(pick)}
                className="group cursor-pointer flex gap-5 sm:gap-8 py-7 first:pt-0"
              >
                {/* Image — left side */}
                <div className="flex-shrink-0 w-32 h-32 sm:w-44 sm:h-44 md:w-52 md:h-52 overflow-hidden bg-stone-100 relative rounded-sm">
                  {getMainImage(pick) ? (
                    <img
                      src={getMainImage(pick)}
                      alt={pick.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200">
                      <Bookmark className="w-8 h-8 text-stone-300" />
                    </div>
                  )}
                </div>

                {/* Text — right side */}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-2">
                    {pick.category && (
                      <span className="text-xs font-bold uppercase tracking-[0.12em] text-terracotta-600">
                        {pick.category}
                      </span>
                    )}
                    <span className="w-1 h-1 rounded-full bg-stone-300" />
                    <span className="text-xs text-stone-400">{formatDate(pick.published_at)}</span>
                  </div>

                  <h3 className="font-serif text-lg sm:text-xl md:text-2xl font-bold text-stone-900 leading-snug mb-2 group-hover:text-terracotta-700 transition-colors line-clamp-2">
                    {pick.title}
                  </h3>

                  {pick.description && (
                    <p className="text-stone-500 text-sm leading-relaxed line-clamp-2 sm:line-clamp-3 mb-2">
                      {pick.description}
                    </p>
                  )}

                  <div className="flex items-center gap-3 text-xs text-stone-400">
                    <span className="font-semibold text-stone-600 uppercase tracking-wide">fashionDisc Editorial</span>
                    <span>·</span>
                    <span>{getReadTime(pick.description)} min read</span>
                  </div>

                  <span className="inline-flex items-center gap-1 text-sm font-medium text-terracotta-600 mt-2 group-hover:gap-2 transition-all">
                    Read Article <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* ── Featured Brands — Editorial Section ── */}
          {promoters.length > 0 && (
            <>
              <div className="border-t border-stone-900 mt-12 pt-8 mb-8">
                <div className="flex items-center gap-3 mb-1">
                  <Star className="w-4 h-4 text-terracotta-500" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-terracotta-600">Promoted</span>
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mb-2">
                  Brands We Love
                </h2>
                <p className="text-stone-400 text-sm max-w-xl">
                  Emerging designers and labels handpicked by our editorial team. Discover the next generation of fashion.
                </p>
              </div>

              <div className="space-y-10 mb-12">
                {promoters.map((brand, idx) => {
                  const products = Array.isArray(brand.featured_products) ? brand.featured_products : [];
                  return (
                    <motion.div
                      key={brand.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + idx * 0.08 }}
                    >
                      {/* Brand header — editorial style */}
                      <div className="flex items-center gap-4 mb-5">
                        {brand.logo_url ? (
                          <img src={brand.logo_url} alt={brand.brand_name} className="w-14 h-14 rounded-full object-cover border-2 border-stone-200" />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-terracotta-400 to-amber-400 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                            {brand.brand_name?.[0] || '?'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif text-xl sm:text-2xl font-bold text-stone-900">{brand.brand_name}</h3>
                          {brand.description && (
                            <p className="text-stone-500 text-sm line-clamp-2 mt-0.5">{brand.description}</p>
                          )}
                        </div>
                        {brand.website_url && (
                          <a
                            href={brand.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-terracotta-600 hover:text-terracotta-700 transition-colors"
                          >
                            Visit Store <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>

                      {/* Products grid — magazine layout */}
                      {products.length > 0 && (
                        <div className={`grid gap-4 ${products.length === 1 ? 'grid-cols-1 max-w-sm' : products.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>
                          {products.map((product, pi) => (
                            <div key={product.id || pi} className="group">
                              {product.image && (
                                <div className="aspect-[3/4] overflow-hidden bg-stone-100 mb-2">
                                  <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                  />
                                </div>
                              )}
                              <p className="font-medium text-stone-800 text-sm truncate">{product.name}</p>
                              {product.price && (
                                <p className="text-terracotta-600 text-sm font-semibold">{product.price}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Mobile visit link */}
                      {brand.website_url && (
                        <a
                          href={brand.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sm:hidden inline-flex items-center gap-1.5 text-sm font-medium text-terracotta-600 mt-3 hover:text-terracotta-700 transition-colors"
                        >
                          Visit Store <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}

                      {/* Divider between brands */}
                      {idx < promoters.length - 1 && (
                        <div className="border-t border-stone-200 mt-8" />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Footer note ── */}
          <div className="border-t border-stone-900 mt-12 pt-6 pb-12 text-center">
            <p className="text-xs text-stone-400 uppercase tracking-[0.2em]">
              Updated Weekly · {getCurrentWeek()} · {picks.length} Article{picks.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
